import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { hubspotApi, microsoftApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { Settings, CheckCircle, XCircle, ExternalLink } from 'lucide-react';

export function IntegrationsPage() {
    const [microsoftStatus, setMicrosoftStatus] = useState<{ configured: boolean; connected: boolean }>({ configured: false, connected: false });
    const [microsoftLoading, setMicrosoftLoading] = useState(false);
    const [hubspotLoading, setHubspotLoading] = useState(false);
    const [attributionForm, setAttributionForm] = useState('');
    const [attributionSaving, setAttributionSaving] = useState(false);
    const [attributionError, setAttributionError] = useState<string | null>(null);
    const [attributionSaved, setAttributionSaved] = useState(false);
    const [searchParams, setSearchParams] = useSearchParams();

    const { hubspotConnected, hubspotConfigured, connectHubSpot, disconnectHubSpot, checkHubSpotStatus } = useAuth();

    useEffect(() => {
        loadMicrosoftStatus();
        loadAttributionForm();

        const msResult = searchParams.get('microsoft');
        if (msResult) {
            if (msResult === 'connected') {
                alert('Microsoft account connected successfully! You can now create Teams meetings.');
            } else if (msResult === 'error') {
                const reason = searchParams.get('reason') || 'Unknown error';
                alert(`Failed to connect Microsoft account: ${reason}`);
            }
            searchParams.delete('microsoft');
            searchParams.delete('reason');
            setSearchParams(searchParams);
        }

        const hsResult = searchParams.get('hubspot');
        if (hsResult) {
            if (hsResult === 'connected') {
                alert('HubSpot account connected successfully! You can now sync contacts and meetings.');
            } else if (hsResult === 'error') {
                const reason = searchParams.get('reason') || 'Unknown error';
                alert(`Failed to connect HubSpot account: ${reason}`);
            }
            searchParams.delete('hubspot');
            searchParams.delete('reason');
            setSearchParams(searchParams);
        }
    }, []);

    const loadMicrosoftStatus = async () => {
        const status = await microsoftApi.getStatus();
        setMicrosoftStatus(status);
    };

    const loadAttributionForm = async () => {
        const status = await hubspotApi.getStatus();
        setAttributionForm(status.attributionFormGuid ?? '');
    };

    const handleAttributionSave = async () => {
        setAttributionSaving(true);
        setAttributionError(null);
        setAttributionSaved(false);
        try {
            await hubspotApi.setAttributionForm(attributionForm.trim());
            setAttributionSaved(true);
        } catch (err: any) {
            setAttributionError(err.message || 'Failed to save the attribution form');
        } finally {
            setAttributionSaving(false);
        }
    };

    const handleMicrosoftConnect = () => {
        window.location.href = microsoftApi.getLoginUrl();
    };

    const handleMicrosoftDisconnect = async () => {
        if (!confirm('Disconnect your Microsoft account? You will no longer be able to create Teams meetings.')) return;
        setMicrosoftLoading(true);
        try {
            await microsoftApi.disconnect();
            setMicrosoftStatus({ ...microsoftStatus, connected: false });
        } catch (err: any) {
            alert(err.message || 'Failed to disconnect');
        } finally {
            setMicrosoftLoading(false);
        }
    };

    const handleHubSpotDisconnect = async () => {
        if (!confirm('Disconnect your HubSpot account? This will stop syncing new bookings to your CRM.')) return;
        setHubspotLoading(true);
        try {
            await disconnectHubSpot();
            await checkHubSpotStatus();
            setAttributionForm('');
            setAttributionSaved(false);
            setAttributionError(null);
        } catch (err: any) {
            alert(err.message || 'Failed to disconnect');
        } finally {
            setHubspotLoading(false);
        }
    };

    return (
        <div className="page container">
            <div className="integrations-header">
                <div className="integrations-header-icon">
                    <Settings size={28} />
                </div>
                <div>
                    <h1>Integrations</h1>
                    <p className="integrations-subtitle">
                        Connect your tools to enhance Let's Meet with powerful features
                    </p>
                </div>
            </div>

            <div className="integrations-grid">
                {microsoftStatus.configured && (
                    <div className={`integration-card ${microsoftStatus.connected ? 'connected' : ''}`}>
                        <div className="integration-card-header">
                            <div className="integration-icon microsoft">
                                <svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor">
                                    <path d="M11.5 3v8.5H3V3h8.5zm0 18H3v-8.5h8.5V21zM21 3v8.5h-8.5V3H21zm0 18h-8.5v-8.5H21V21z"/>
                                </svg>
                            </div>
                            <div className="integration-info">
                                <h3>Microsoft Teams</h3>
                                <p>Create Teams meetings automatically when scheduling</p>
                            </div>
                        </div>
                        
                        <div className="integration-card-status">
                            {microsoftStatus.connected ? (
                                <span className="status-badge connected">
                                    <CheckCircle size={14} />
                                    Connected
                                </span>
                            ) : (
                                <span className="status-badge disconnected">
                                    <XCircle size={14} />
                                    Not connected
                                </span>
                            )}
                        </div>

                        <div className="integration-card-actions">
                            {microsoftStatus.connected ? (
                                <button
                                    className="btn btn-secondary"
                                    onClick={handleMicrosoftDisconnect}
                                    disabled={microsoftLoading}
                                >
                                    {microsoftLoading ? 'Disconnecting...' : 'Disconnect'}
                                </button>
                            ) : (
                                <button
                                    className="btn btn-primary"
                                    onClick={handleMicrosoftConnect}
                                    style={{ background: '#0078d4' }}
                                >
                                    <ExternalLink size={16} />
                                    Connect Microsoft
                                </button>
                            )}
                        </div>

                        <div className="integration-features">
                            <span className="feature-tag">Video Meetings</span>
                            <span className="feature-tag">Calendar Sync</span>
                        </div>
                    </div>
                )}

                {hubspotConfigured && (
                    <div className={`integration-card ${hubspotConnected ? 'connected' : ''}`}>
                        <div className="integration-card-header">
                            <div className="integration-icon hubspot">
                                <svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor">
                                    <path d="M17.5 8.5a2 2 0 0 0-2-2v-2a1 1 0 0 0-2 0v2a2 2 0 0 0 0 4 2 2 0 0 0 0 4v2a1 1 0 0 0 2 0v-2a2 2 0 0 0 2-2 2 2 0 0 0-2-2 2 2 0 0 0 2-2zm-2 6a1 1 0 1 1 0-2 1 1 0 0 1 0 2zm0-4a1 1 0 1 1 0-2 1 1 0 0 1 0 2zM8.5 12a3.5 3.5 0 1 0-7 0 3.5 3.5 0 0 0 7 0zm-3.5 2a2 2 0 1 1 0-4 2 2 0 0 1 0 4zm7 1h-3a.5.5 0 0 0 0 1h3a.5.5 0 0 0 0-1z"/>
                                </svg>
                            </div>
                            <div className="integration-info">
                                <h3>HubSpot CRM</h3>
                                <p>Sync contacts and meetings to your CRM automatically</p>
                            </div>
                        </div>
                        
                        <div className="integration-card-status">
                            {hubspotConnected ? (
                                <span className="status-badge connected">
                                    <CheckCircle size={14} />
                                    Connected
                                </span>
                            ) : (
                                <span className="status-badge disconnected">
                                    <XCircle size={14} />
                                    Not connected
                                </span>
                            )}
                        </div>

                        <div className="integration-card-actions">
                            {hubspotConnected ? (
                                <button
                                    className="btn btn-secondary"
                                    onClick={handleHubSpotDisconnect}
                                    disabled={hubspotLoading}
                                >
                                    {hubspotLoading ? 'Disconnecting...' : 'Disconnect'}
                                </button>
                            ) : (
                                <button
                                    className="btn btn-primary"
                                    onClick={connectHubSpot}
                                    style={{ background: '#ff7a59' }}
                                >
                                    <ExternalLink size={16} />
                                    Connect HubSpot
                                </button>
                            )}
                        </div>

                        {hubspotConnected && (
                            <div className="form-group">
                                <label className="form-label" htmlFor="hubspot-attribution-form">
                                    Attribution form GUID
                                </label>
                                <input
                                    id="hubspot-attribution-form"
                                    className={`form-input ${attributionError ? 'error' : ''}`}
                                    type="text"
                                    value={attributionForm}
                                    placeholder="1a2b3c4d-5e6f-7a8b-9c0d-1e2f3a4b5c6d"
                                    onChange={(e) => {
                                        setAttributionForm(e.target.value);
                                        setAttributionError(null);
                                        setAttributionSaved(false);
                                    }}
                                />
                                <p className="form-hint">
                                    A HubSpot form in your portal (email, first name, last name). When set, bookings that
                                    carry a HubSpot tracking cookie are submitted through it so HubSpot resolves the
                                    original source from the visitor's own session. Leave blank to use traffic source
                                    detection instead.
                                </p>
                                {attributionError && <p className="form-error">{attributionError}</p>}
                                {attributionSaved && !attributionError && (
                                    <p className="form-hint">Saved.</p>
                                )}
                                <button
                                    className="btn btn-secondary"
                                    onClick={handleAttributionSave}
                                    disabled={attributionSaving}
                                    style={{ marginTop: 'var(--space-2)' }}
                                >
                                    {attributionSaving ? 'Saving...' : 'Save'}
                                </button>
                            </div>
                        )}

                        <div className="integration-features">
                            <span className="feature-tag">Contact Sync</span>
                            <span className="feature-tag">Meeting Logs</span>
                        </div>
                    </div>
                )}

                {!microsoftStatus.configured && !hubspotConfigured && (
                    <div className="no-integrations-card">
                        <Settings size={48} style={{ color: 'var(--color-gray-300)' }} />
                        <h3>No Integrations Available</h3>
                        <p>Contact your administrator to enable integrations for your account.</p>
                    </div>
                )}
            </div>
        </div>
    );
}
